import { Users, Trash2, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { TournamentTeam } from '../interfaces/tournaments';
import { STAFF_ROLE_LABELS } from '../interfaces/tournaments';

interface TeamRosterCardProps {
  team: TournamentTeam;
  variant?: 'admin' | 'public';
  showStaff?: boolean;
  tournamentStatus?: string;
  onDeleteTeam?: (teamId: string) => void;
  onEditTeam?: (team: TournamentTeam) => void;
  onUpdateSeed?: (teamId: string, seed: number | null) => Promise<void>;
}

function PlayerRow({
  player,
  index,
  variant = 'admin',
}: {
  player: NonNullable<TournamentTeam['players']>[number];
  index: number;
  variant?: 'admin' | 'public';
}) {
  if (variant === 'public') {
    const riotTag = player.riot_id || player.player_ign;
    if (!riotTag) return null;

    return (
      <div
        key={player.id || index}
        className="px-2.5 py-2 rounded-md bg-primary/5 border border-primary/10"
      >
        <p className="text-xs font-mono text-[#FF4655] truncate">{riotTag}</p>
      </div>
    );
  }

  return (
    <div
      key={player.id || index}
      className="px-2.5 py-2 rounded-md bg-primary/5 border border-primary/10"
    >
      <p className="text-xs font-medium text-foreground truncate">{player.player_name}</p>
      {player.riot_id ? (
        <p className="text-xs font-mono text-primary truncate mt-0.5">{player.riot_id}</p>
      ) : player.player_ign ? (
        <p className="text-xs font-mono text-muted-foreground truncate mt-0.5">{player.player_ign}</p>
      ) : null}
      {player.discord_tag ? (
        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{player.discord_tag}</p>
      ) : null}
    </div>
  );
}

export function TeamRosterCard({
  team,
  variant = 'admin',
  showStaff = true,
  tournamentStatus,
  onDeleteTeam,
  onEditTeam,
  onUpdateSeed,
}: TeamRosterCardProps) {
  return (
    <Card className="bg-primary/5 backdrop-blur-xl border-primary/10 hover:bg-primary/10 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          {team.logo_url ? (
            <img 
              src={team.logo_url} 
              alt={`${team.team_name} logo`} 
              width={48}
              height={48}
              loading="lazy"
              className="w-12 h-12 rounded-full object-cover border border-primary/20" 
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary/60" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-foreground truncate">{team.team_name}</h4>
              {team.team_tag && (
                <Badge variant="outline" className="text-xs shrink-0">
                  {team.team_tag}
                </Badge>
              )}
            </div>
            {variant === 'admin' && team.captain_name && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                Capitán: {team.captain_name}
              </p>
            )}
          </div>
        </div>

        {tournamentStatus === 'registration' && onUpdateSeed && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-muted-foreground">Seed:</span>
            <Input
              type="number"
              min={1}
              className="h-7 w-16 text-xs"
              placeholder="#"
              defaultValue={team.seed ?? ''}
              onBlur={async (e) => {
                const val = e.target.value ? parseInt(e.target.value, 10) : null;
                await onUpdateSeed(team.id, val);
              }}
            />
          </div>
        )}
        {team.seed != null && tournamentStatus !== 'registration' && (
          <Badge variant="outline" className="mb-2 text-xs">
            Seed #{team.seed}
          </Badge>
        )}

        {team.players && team.players.length > 0 ? (
          <div className="border-t border-primary/10 pt-3 mt-1">
            <p className="text-xs text-muted-foreground mb-2 font-medium">
              Jugadores ({team.players.length})
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {team.players.map((player, index) => (
                <PlayerRow key={player.id || index} player={player} index={index} variant={variant} />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground border-t border-primary/10 pt-3 mt-1">
            Sin jugadores registrados
          </p>
        )}

        {showStaff && team.staff && team.staff.length > 0 && (
          <div className="border-t border-primary/10 pt-3 mt-3">
            <p className="text-xs text-muted-foreground mb-2 font-medium">Staff</p>
            <div className="space-y-1.5">
              {team.staff.map((member, index) => (
                <div
                  key={member.id || index}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="font-medium truncate">{member.staff_name}</span>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {STAFF_ROLE_LABELS[member.staff_role] || member.staff_role}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {(onDeleteTeam || onEditTeam) && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-primary/10">
            <Badge
              variant="outline"
              className={
                team.status === 'confirmed'
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                  : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
              }
            >
              {team.status === 'confirmed' ? 'Confirmado' : 'Registrado'}
            </Badge>
            <div className="flex items-center gap-2">
              {onEditTeam && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditTeam(team)}
                  className="h-8 px-3"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar
                </Button>
              )}
              {onDeleteTeam && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDeleteTeam(team.id)}
                  className="h-8 px-3"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface TournamentTeamsListProps {
  teams: TournamentTeam[];
  variant?: 'admin' | 'public';
  emptyTitle?: string;
  emptyDescription?: string;
  gridClassName?: string;
  showStaff?: boolean;
  tournamentStatus?: string;
  onDeleteTeam?: (teamId: string) => void;
  onEditTeam?: (team: TournamentTeam) => void;
  onUpdateSeed?: (teamId: string, seed: number | null) => Promise<void>;
}

export function TournamentTeamsList({
  teams,
  emptyTitle = 'No hay equipos registrados aún',
  emptyDescription = 'Registra equipos para comenzar el torneo',
  gridClassName = 'grid grid-cols-1 md:grid-cols-2 gap-4',
  ...cardProps
}: TournamentTeamsListProps) {
  if (!teams || teams.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 mx-auto mb-4 bg-primary/5 backdrop-blur-xl border border-primary/20 rounded-full flex items-center justify-center">
          <Users className="w-8 h-8 text-primary/60" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">{emptyTitle}</h3>
        <p className="text-muted-foreground/80">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className={gridClassName}>
      {teams.map((team) => (
        <TeamRosterCard key={team.id} team={team} {...cardProps} />
      ))}
    </div>
  );
}
